import { axiosClient } from '@/shared/apis/config';
import { ApiResponse, CustomQueryOptions } from '@/shared/types';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { DtoProjectStatus, DtoProjectErrorType } from '@/shared/constants';

// 진행률 스키마
const StatusProgressSchema = z.object({
  current: z.number(),
  total: z.number(),
  label: z.string(),
  statusKey: z.string(),
});

// 진행 중인 프로젝트 DTO
const InProgressProjectSchema = z.object({
  projectId: z.number(),
  status: z.nativeEnum(DtoProjectStatus),
  errorCode: z.nativeEnum(DtoProjectErrorType).nullable(),
  statusProgress: StatusProgressSchema.nullable(),
  isCompleted: z.boolean(),
  isRetryable: z.boolean().default(false),
  failedStep: z.string().nullable().default(null),
  retryCount: z.number().default(0),
});

// 응답 스키마
const InProgressProjectListSchema = z.object({
  projects: z.array(InProgressProjectSchema),
  inProgressCount: z.number(),
});

export type InProgressProject = z.infer<typeof InProgressProjectSchema>;
export type InProgressProjectList = z.infer<typeof InProgressProjectListSchema>;

type ResponseType = ApiResponse<InProgressProjectList>;

/**
 * v2.0 진행 중인 프로젝트 상태 조회 API
 *
 * 폴링용 경량 API - 프로젝트 생성/분석 진행 상태만 조회
 */
export const getProjectsInProgressApi = async (
  projectIds: number[],
): Promise<ResponseType> => {
  if (projectIds.length === 0) {
    return {
      status: 'OK',
      code: 200,
      message: 'Success',
      data: {
        projects: [],
        inProgressCount: 0,
      },
    };
  }

  // Spring Boot는 repeated params 형태를 기대: ?projectIds=1&projectIds=2&projectIds=3
  const params = new URLSearchParams();
  projectIds.forEach((id) => params.append('projectIds', id.toString()));

  const response = await axiosClient.get<ResponseType>(
    `/v2.0/project/in-progress?${params.toString()}`,
  );

  return response.data;
};

export const QueryKeyUseGetProjectsInProgress =
  'getProjectsInProgress' as const;

interface UseGetProjectsInProgressOptions extends CustomQueryOptions<ResponseType> {
  projectIds: number[];
  /** 폴링 간격 (ms). false면 폴링 안 함 */
  pollingInterval?: number | false;
}

/**
 * 진행 중인 프로젝트 상태 폴링 훅
 *
 * @example
 * ```tsx
 * const { data } = useGetProjectsInProgressApi({
 *   projectIds: [1, 2, 3],
 *   pollingInterval: 5000, // 5초마다 폴링
 * });
 * ```
 */
export const useGetProjectsInProgressApi = ({
  projectIds,
  pollingInterval = false,
  ...options
}: UseGetProjectsInProgressOptions) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [
      QueryKeyUseGetProjectsInProgress,
      projectIds,
      ...(queryKey ?? []),
    ],
    queryFn: () => getProjectsInProgressApi(projectIds),
    enabled: projectIds.length > 0,
    refetchInterval: pollingInterval,
    ...rest,
  });
};
